/**
 * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
 * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
 *
 * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
 */

import pluginFactory from 'taoTests/runner/plugin';
import { testSessionStatus } from '../../../session/sessionStates.js';
import { getTestSessionStatusStore } from '../../../testsStateStore.js';
import ReviewNavigator from './ReviewNavigator.svelte';
import OverlayHeaderBar from '../../../layout/overlay/OverlayHeaderBar.svelte';
import TestOverviewContent from './overview/TestOverviewContent.svelte';
import TestOverviewBottomBar from './overview/TestOverviewBottomBar.svelte';
import { __ } from '@oat-sa-private/ui-core';
import { tick, mount, unmount } from 'svelte';
import { createProps } from '../../../../svelteUtils/props.svelte.js';

/**
 * the navigator plugin handles :
 *  - the next/previous buttons
 *  - the progress visualization
 *  - the overview
 */
export default pluginFactory({
    name: 'reviewNavigator',

    install() {
        const testRunner = this.getTestRunner();
        const testConfig = testRunner.getConfig();
        const options = testRunner.getOptions();

        /**
         * Set the session status to "overlay"
         */
        this.setOverlayStatus = () => {
            getTestSessionStatusStore(testConfig.serviceCallId).set(testSessionStatus.overlay);
        };

        /**
         * Check whether the current status is overlay
         * @returns {Boolean}
         */
        this.isOverlayStatus = () =>
            getTestSessionStatusStore(testConfig.serviceCallId).get() === testSessionStatus.overlay;

        /**
         * Check whether the current status is loading
         * @returns {Boolean}
         */
        this.isLoadingStatus = () =>
            getTestSessionStatusStore(testConfig.serviceCallId).get() === testSessionStatus.loading;

        /**
         * Take control of the TestLayout's Overlay
         * Create and inject the components which make up the Overview
         */
        this.createOverview = () => {
            // inject the Overlay content in defined slots
            const areaBroker = this.getAreaBroker();
            const headerSlot = areaBroker.getOverlayHeaderArea();
            const contentSlot = areaBroker.getOverlayContentArea();
            const footerSlot = areaBroker.getOverlayFooterArea();
            areaBroker.clearAreasContent(['overlayHeader', 'overlayContent', 'overlayFooter']);

            const overviewTitle = __('Overview');

            const onClose = () => {
                this.toggleOverview(false);
            };
            const onMove = ({ position }) => {
                if (testRunner.getTestContext().itemPosition !== position) {
                    testRunner.jump(position);
                }
                this.toggleOverview(false);
            };
            const onFinish = () => {
                testRunner.finish();
            };

            this.overlayHeader = mount(OverlayHeaderBar, {
                target: headerSlot,
                props: {
                    heading: overviewTitle.toUpperCase(),
                    onClose
                }
            });
            this.overlayContent = mount(TestOverviewContent, {
                target: contentSlot,
                props: {
                    serviceCallId: testConfig.serviceCallId,
                    showScore: options.review && options.review.showScore,
                    onMove
                }
            });
            this.overlayFooter = mount(TestOverviewBottomBar, {
                target: footerSlot,
                props: {
                    isFinalDelivery: !testConfig?.batteryContext?.nextDeliveryExecutionUrl?.length,
                    onClose,
                    onFinish
                }
            });
        };

        /**
         * Destroy the Overview components
         */
        this.destroyOverview = () => {
            if (this.overlayHeader) {
                unmount(this.overlayHeader);
            }
            if (this.overlayContent) {
                unmount(this.overlayContent);
            }
            if (this.overlayFooter) {
                unmount(this.overlayFooter);
            }
            this.overlayHeader = null;
            this.overlayContent = null;
            this.overlayFooter = null;
        };

        /**
         * Open/close overview.
         * Do not call it when navigating away with jump/next.
         * @param {boolean} open
         */
        this.toggleOverview = open => {
            const testContext = testRunner.getTestContext();
            const itemIdentifier = testContext.itemIdentifier;
            const areaBroker = testRunner.getAreaBroker();

            // open from any status
            if (open) {
                testRunner.on('disableitem.overview', () => {
                    testRunner.off('disableitem.overview');

                    this.setOverlayStatus(); // opens Overlay
                    this.createOverview();

                    areaBroker.getOverlayHeaderArea().querySelector('h2').focus();
                });
                testRunner.disableItem(itemIdentifier);

                // close
            } else if (this.isOverlayStatus()) {
                testRunner.on('enableitem.overview', () => {
                    testRunner.off('enableitem.overview');

                    tick().then(() => this.destroyOverview());

                    const overviewButton = areaBroker.getNavigationArea().querySelector('button[name="overview"]');
                    if (overviewButton) {
                        overviewButton.focus();
                    }
                });
                testRunner.enableItem(itemIdentifier); //it restores testSessionStatus itself
            }
        };
    },

    init() {
        const testRunner = this.getTestRunner();
        const areaBroker = testRunner.getAreaBroker();
        const navigationArea = areaBroker.getNavigationArea();
        const testConfig = testRunner.getConfig();
        const testSessionStatusStore = getTestSessionStatusStore(testConfig.serviceCallId);

        testRunner
            .on('enablenav', () => {
                this.enable();
            })
            .on('disablenav', () => {
                this.disable();
            });

        this.unsubscribeFromStatusChanges = testSessionStatusStore.subscribe(status => {
            /**
             * Hide navigation from screenreader, when overlay is open.
             * The functionality is here, because empty <nav /> should be hidden by default.
             */
            if (navigationArea) {
                if (status === testSessionStatus.overlay) {
                    navigationArea.setAttribute('aria-hidden', 'true');
                } else {
                    navigationArea.removeAttribute('aria-hidden');
                }
            }
        });
    },

    render() {
        const testRunner = this.getTestRunner();
        const areaBroker = testRunner.getAreaBroker();
        const testConfig = testRunner.getConfig();
        const options = testRunner.getOptions();

        const onMove = ({ direction, scope, position }) => {
            if (direction === 'next') {
                testRunner.next(scope);
            } else if (direction === 'previous') {
                testRunner.previous(scope);
            } else {
                if (testRunner.getTestContext().itemPosition !== position) {
                    testRunner.jump(position);
                }
            }
        };
        const onOverview = () => {
            this.toggleOverview(true);
        };
        const onFinish = () => {
            testRunner.finish();
        };

        this.reviewNavigatorProps = createProps({
            serviceCallId: testConfig.serviceCallId,
            disabled: true,
            showScore: options.review && options.review.showScore,
            isFinalDelivery: !testConfig?.batteryContext?.nextDeliveryExecutionUrl?.length,
            onMove,
            onOverview,
            onFinish
        });

        //render the navigator component
        this.reviewNavigator = mount(ReviewNavigator, {
            target: areaBroker.getNavigationArea(),
            props: this.reviewNavigatorProps
        });
    },

    enable() {
        if (this.reviewNavigator) {
            this.reviewNavigatorProps.disabled = false;
        }
    },

    disable() {
        if (this.reviewNavigator) {
            this.reviewNavigatorProps.disabled = true;
        }
    },

    destroy() {
        if (this.unsubscribeFromStatusChanges) {
            this.unsubscribeFromStatusChanges();
        }
        if (this.reviewNavigator) {
            unmount(this.reviewNavigator);
        }
        this.destroyOverview();
    }
});
